


import {
  defineComponent,
  computed,
  onMounted,
  reactive,
  ref,
} from '@vue/composition-api';

import { SearchState } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import RealtimeView from './components/RealtimeView/index.vue';
import ReportsView from './components/ReportsView/index.vue';
import ManualDownloadLinks from './components/common/ManualDownloadLinks/index.vue';
import MapMiscInfo from './components/common/MapMiscInfo.vue';
import { dtFormat } from '@/lib/dateHelper';
import { initSearchConds, validSearchDateRange, loadHokkaidoAirportMtxs } from '@/components/ExternalReports/HokkaidoAirport/utils';
import { formatReportsToCSV, createCSVBlob } from '@/components/ExternalReports/HokkaidoAirport/ReportPrint/utils';
import { downloadBlob } from '@/lib/downloadHelper';
import {
  VIEW_MODE_REALTIME,
  VIEW_MODE_REPORT,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import {
  HAP_ITEMS_PER_PAGE_DEFAULT,
  HAP_CURRENT_PAGE_DEFAULT,
} from '@/consts/externalReports/hokkaidoAirport/report_print';
import useHokkaidoAirportCars, {
  provideHokkaidoAirportCars,
} from '@/components/ExternalReports/HokkaidoAirport/composables/useCars';
import {
  provideHokkaidoAirportReports,
  useHokkaidoAirportReports,
} from '@/components/ExternalReports/HokkaidoAirport/composables/useReports';
import { useRequestGuard } from '@/composables/useRequestGuard';
import type {
  ExternalReportsKeyName,
} from '@/models/apis/externalReports/external_report_settings';
import type {
  HokkaidoAirportChoices,
  HokkaidoAirportLabelPc,
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import {
  buildTrackVisibilityQuery,
  isHokkaidoAirportExternalReportSettings,
} from '@/lib/externalReports/externalReportHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/hokkaidoAirport';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import { EXTERNAL_REPORT_ABILITY_DRIVER } from '@/consts/externalReports/external_reports';

interface SelectOption {
  id: number;
  name: string;
}

export default defineComponent({
  setup() {
    const viewMode = ref<string>('');
    // 報告書モードの走行軌跡の表示状態。地図の描画と印刷画面への引き継ぎに使う
    const isReportTrackVisible = ref<boolean>(true);

    const searchState = reactive<SearchState>({
      searchConds: {
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
      decidedSearchConds: {
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const store = useStore();
    const userState = store.state.user;
    const isRealtimeMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REALTIME;
    });
    const isReportMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REPORT;
    });
    const hokkaidoAirportUserSettings = ref<HokkaidoAirportSettings | null>(null);
    const externalReportAbility = computed<ExternalReportAbility | null>(() => {
      return userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
    });
    const hokkaidoAirportChoices = computed<HokkaidoAirportChoices>(() => {
      if (!hokkaidoAirportUserSettings.value?.choices) return {} as HokkaidoAirportChoices;

      return hokkaidoAirportUserSettings.value.choices;
    });
    const workHans = computed<SelectOption[]>(() => {
      if (!hokkaidoAirportChoices.value?.work_hans) return [];

      return hokkaidoAirportChoices.value.work_hans.map((e, index) => ({ id: index, name: e }));
    });
    const isDateParamOk = computed<boolean>(() => {
      return validSearchDateRange(searchState.searchConds);
    });
    const statusChoices = computed<ExternalReportsKeyName[]>(() => {
      if (!hokkaidoAirportChoices.value?.status) return [];
      const hokkaidoAirportStatus = hokkaidoAirportChoices.value?.status || {};
      return [
        { key: '全て', name: '全て' },
        { key: hokkaidoAirportStatus.running.key, name: hokkaidoAirportStatus.running.name },
        { key: hokkaidoAirportStatus.working.key, name: hokkaidoAirportStatus.working.name },
        { key: hokkaidoAirportStatus.stopped.key, name: hokkaidoAirportStatus.stopped.name },
      ];
    });
    const pcLabels = computed<HokkaidoAirportLabelPc>(() => {
      return hokkaidoAirportUserSettings.value?.pc || {
        title: {},
        button: {},
        field_label: {},
        table_header: {},
      } as HokkaidoAirportLabelPc;
    });

    const { router } = useRoute();

    onMounted(async() => {
      await doRequest(async() => {
        await waitForUserAndMasters();
      }, '初期化に失敗しました。');
      const externalReportSetting = window.master.externalReportSettings?.find(
        (setting) => setting.external_report_type === EXTERNAL_REPORT_TYPE,
      );
      if (!externalReportSetting) return;

      hokkaidoAirportUserSettings.value = isHokkaidoAirportExternalReportSettings(externalReportSetting)
        ? externalReportSetting
        : null;
      if (!externalReportAbility.value || !hokkaidoAirportUserSettings.value) {
        router.replace({ name: 'Login' });
        return;
      }
      searchState.searchConds = initSearchConds();
      searchState.decidedSearchConds = { ...searchState.searchConds };
      viewMode.value = VIEW_MODE_REALTIME;
      const abilities = hokkaidoAirportUserSettings.value.choices.abilities;
      const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
      if (!ability) {
        router.replace({ name: 'Login' });
        return;
      }

      if (ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
        router.replace({ name: 'Login' });
      }
    });

    const {
      filterCars,
      clearCars,
    } = (() => {
      const injectedState = provideHokkaidoAirportCars(searchState);
      return useHokkaidoAirportCars(injectedState);
    })();

    const {
      reportState,
      searchReports,
      clearReports,
    } = (() => {
      const injectedState = provideHokkaidoAirportReports(searchState);
      return useHokkaidoAirportReports(injectedState);
    })();

    const isReportOutputDisabled = computed<boolean>(() => {
      return !isDateParamOk.value || reportState.reports.length === 0 || isRequesting.value;
    });

    const downloadCSV = async() => {
      if (reportState.reports.length === 0) return;

      await doRequest(async() => {
        await loadHokkaidoAirportMtxs(reportState.reports);
        const timestamp = dtFormat(new Date(), 'yyyymmdd_HHMMSS');
        const filename = `${pcLabels.value.title.report_detail_title}_${timestamp}.csv`;
        const csvContent = formatReportsToCSV(reportState.reports, hokkaidoAirportUserSettings.value);
        const blob = createCSVBlob(csvContent);
        await downloadBlob(blob, filename);
      }, 'CSVダウンロードに失敗しました。再度操作を行ってください');
    };

    const changeViewMode = async(mode: string) => {
      if (mode === viewMode.value) return;

      searchState.searchConds = initSearchConds();
      viewMode.value = mode;
      if (mode === VIEW_MODE_REALTIME) {
        clearReports();
      }
      if (mode === VIEW_MODE_REPORT) {
        clearCars();
        // 写真種別と同じく、報告書モードに入り直したら走行軌跡は表示に戻す
        isReportTrackVisible.value = true;
      }
    };

    const onChangeReportTrackVisibility = (isVisible: boolean): void => {
      isReportTrackVisible.value = isVisible;
    };

    const onClickReportPrintAllButton = () => {
      const routeObj = {
        name: 'PrintExternalHokkaidoAirportReportAll',
        params: {
          dateFrom: dtFormat(searchState.searchConds.dateFrom, 'yyyy-mm-dd'),
          dateTo: dtFormat(searchState.searchConds.dateTo, 'yyyy-mm-dd'),
          hanName: searchState.searchConds.hanName || 'all',
          currentPage: HAP_CURRENT_PAGE_DEFAULT.toString(),
          itemsPerPage: HAP_ITEMS_PER_PAGE_DEFAULT.toString(),
        },
        query: buildTrackVisibilityQuery(isReportTrackVisible.value),
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };

    return {
      searchState,
      userState,
      isRequesting,
      statusChoices,
      hokkaidoAirportUserSettings,
      isReportTrackVisible,
      // const
      VIEW_MODE_REALTIME,
      VIEW_MODE_REPORT,
      // computed
      isRealtimeMode,
      isReportMode,
      workHans,
      isDateParamOk,
      pcLabels,
      isReportOutputDisabled,
      // methods
      changeViewMode,
      filterCars,
      searchReports,
      onClickReportPrintAllButton,
      onChangeReportTrackVisibility,
      downloadCSV,
    };
  },
  components: {
    RealtimeView,
    ReportsView,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
